/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql } from '@apollo/client';
import {
  cond,
  renderDefaultError,
  renderDefaultLoading,
  SmartComponentProps
} from '@exo/frontend-common-utils';
import { useProductSearch } from '../../hooks/useProductSearch';
import { defaultFormatLabel, formatFacetData, getSelectedFacetsList } from '../../common/filters';
import { Facet, FacetEntry } from '../../model/types';
import { useEffect } from 'react';
import { useSessionContext } from '@exo/frontend-common-session-context';

// TODO: Add support for CmmFacetMode
export const SearchFiltersContainer = ({
  term,
  currency,
  formatLabel = defaultFormatLabel,
  selectedFacets,
  excludeCategories = true,
  onLoad,
  render,
  renderError = renderDefaultError,
  renderLoading = renderDefaultLoading,
  hasPriceFilters
}: Props) => {
  const { loading, data, error } = useProductSearch<Facets>(
    { searchTerm: term, facets: [], currency },
    SearchFiltersContainer.fragment(!!hasPriceFilters)
  );

  const session = useSessionContext();
  const AvailabilityObj: any = {
    __typename: 'CmmFacet',
    name: 'Availability',
    multiSelect: true,
    type: 'GENERAL',
    entries: [
      {
        __typename: 'CmmFacetEntry',
        label: session.language === 'en' ? 'In Stock' : 'En stock',
        count: null,
        code: 'InventoryFacet%3DInStock',
        state: 'unselected',
        type: 'select',
        extendedLabel: null
      },
      {
        __typename: 'CmmFacetEntry',
        label: session.language === 'en' ? 'Out of Stock' : 'En rupture de stock',
        count: null,
        code: 'InventoryFacet%3DOutofStock',
        state: 'unselected',
        type: 'select',
        extendedLabel: null
      }
    ]
  };
  useEffect(() => {
    if (!data || !onLoad) return;
    onLoad({
      selectedFacets:
        data?.products?.facets?.length! > 0 &&
        selectedFacets?.length! > 0 &&
        getSelectedFacetsList(
          [AvailabilityObj, ...data?.products?.facets!],
          selectedFacets,
          formatLabel
        )
    });
  }, [data, selectedFacets?.join(',')]);

  if (loading) return renderLoading();
  if (error) return renderError(error);
  if (!data) return null;

  const facets = data?.products?.facets?.filter(
    eachFacet =>
      eachFacet?.name !== 'ParentCatalogGroup' &&
      eachFacet?.name !== 'Price' &&
      eachFacet?.name !== 'Price (USD)' 
  );
  return render({
    selectedFacets:
      facets?.length! > 0 &&
      getSelectedFacetsList([AvailabilityObj, ...facets!], selectedFacets, formatLabel),
    facets:
      facets?.length! > 0 &&
      formatFacetData([AvailabilityObj, ...facets!], excludeCategories, formatLabel)
  });
};

type Facets = {
  facets: Facet[];
};

SearchFiltersContainer.fragment = (priceFilters: boolean) => gql`
  fragment SearchFilter on PrdResultConnection {
    facets {
      name
      multiSelect
      type
      entries {
        label
        count
        code
        state
        type
        ${cond(
          priceFilters,
          `
          extendedLabel {
          ... on CmmFacetExtendedLabelMonetaryAmount {
             amount {
               value
               currency
             }
           }

           ... on CmmFacetExtendedLabelMonetaryAmountRange {
             amountLow {
              value
               currency
             }
             amountHigh {
               value
               currency
             }
           }
         }
        `
        )}
        
      }
    }
  }
`;

type Props = SmartComponentProps<{
  term: string;
  currency: string;
  hasPriceFilters?: boolean;
  selectedFacets?: string[];
  formatLabel?: (facet: Facet, entry: FacetEntry) => string;
  excludeCategories?: boolean;
  onLoad?: (d: any) => void;
  render: (props: SearchFiltersContainerRenderProps) => JSX.Element;
}>;

export type SearchFiltersContainerRenderProps = {
  selectedFacets?: ReturnType<typeof getSelectedFacetsList>;
  facets: ReturnType<typeof formatFacetData>;
};
